// Tearing-safe external store for room state, read via React's useSyncExternalStore.
//
// Not `use-context-selector`: it reads refs during render and self-dispatches,
// and under React 19's more concurrent rendering that pattern can tear (different
// selectors observing different versions of state in one commit), which shows
// up as the wrong meme answer / colors landing in the wrong ad-lib slots.
// `useSyncExternalStore` is React's purpose-built primitive for consistent,
// tearing-free reads from an external store.

import { useSyncExternalStore } from 'react';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';
import type {
  RoomState,
  RoomPlayer,
  RoomTeam,
} from '@airhead-games/shared/types';

interface RoomStoreSnapshot {
  roomState: RoomState | null;
  myPlayer: RoomPlayer | null;
  myTeam: RoomTeam | null;
}

let snapshot: RoomStoreSnapshot = {
  roomState: null,
  myPlayer: null,
  myTeam: null,
};

const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): RoomStoreSnapshot {
  return snapshot;
}

// --- Writers (called by RoomStateProvider) ---

export function setRoomStoreRoomState(roomState: RoomState | null): void {
  if (snapshot.roomState === roomState) return;
  snapshot = { ...snapshot, roomState };
  emitChange();
}

export function setRoomStoreMyPlayer(myPlayer: RoomPlayer | null): void {
  if (snapshot.myPlayer === myPlayer) return;
  snapshot = { ...snapshot, myPlayer };
  emitChange();
}

export function setRoomStoreMyTeam(myTeam: RoomTeam | null): void {
  if (snapshot.myTeam === myTeam) return;
  snapshot = { ...snapshot, myTeam };
  emitChange();
}

// --- Imperative getters (non-reactive reads, e.g. from WS event handlers) ---

export function getRoomStoreRoomState(): RoomState | null {
  return snapshot.roomState;
}

export function getRoomStoreMyPlayer(): RoomPlayer | null {
  return snapshot.myPlayer;
}

// --- Readers ---

function shallowEqual<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== 'object' ||
    a === null ||
    typeof b !== 'object' ||
    b === null
  ) {
    return false;
  }
  const keysA = Object.keys(a as Record<string, unknown>);
  const keysB = Object.keys(b as Record<string, unknown>);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) =>
    Object.is(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key]
    )
  );
}

/**
 * Select a slice of room state. Re-renders only when the selected value
 * changes (shallow equality), matching the previous use-context-selector
 * granularity, but with tearing-safe consistency under concurrent rendering.
 */
export function useRoomStateSnapshot<T>(
  selector: (state: RoomState | null) => T,
  isEqual: (a: T, b: T) => boolean = shallowEqual
): T {
  return useSyncExternalStoreWithSelector(
    subscribe,
    getSnapshot,
    getSnapshot,
    (snap) => selector(snap.roomState),
    isEqual
  );
}

export function useRoomStoreRoomState(): RoomState | null {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot().roomState,
    () => getSnapshot().roomState
  );
}

export function useRoomStoreMyPlayer(): RoomPlayer | null {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot().myPlayer,
    () => getSnapshot().myPlayer
  );
}

export function useRoomStoreMyTeam(): RoomTeam | null {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot().myTeam,
    () => getSnapshot().myTeam
  );
}
